import { reactive, computed } from 'vue';
import { useClient } from '@/lib/_API/useClient';
import store from '@/store';
import cloneDeep from 'lodash/cloneDeep';

/**
 * 출/도착지 데이터 모델
 * 순환 참조를 피하기 위해 useMassParcel.ts에 직접 정의
 */
const MODEL_PIN = (): outbound.pin => {
  return cloneDeep({
    name: '',
    phoneNumber: {
      value: '',
      isValidate: null,
    },
    jibunAddress: '',
    roadAddress: '',
    detailAddress: '',
    dongName: '',
    postalCode: '',
    latitude: 0,
    longitude: 0,
    pickupTime: '',
  });
};

export default function useMassParcel() {
  return {
    state,
    ...useMassParcelAction(),
    ...useMassParcelUpdate(),
    ...useMassParcelGetters(),
  };
}

/**
 * 다량택배접수 전용 상태 값
 */
export const state = reactive<outbound.mass.parcel.state>({
  origin: MODEL_PIN(),
  list: [],
  isBusy: false,
  policy: false,
  customFields: [],
  customerDropOffMethod: '',
  onsitePickupRequestedAt: undefined,
  onsitePickupClientComment: undefined,
});

/**
 *
 *
 * 다량택배접수 상태 값을 단순 업데이트 합니다.
 *
 *
 */
export function useMassParcelUpdate() {
  /**
   * 출발지 정보 업데이트
   */
  const updateOrigin = <T extends outbound.pin, K extends keyof T>(key: K, value: T[K]) => {
    state.origin[key as string] = value;
  };

  /**
   * 출발지 정보 초기화
   */
  const resetOrigin = <T extends outbound.pin, K extends keyof T>() => {
    const key = Object.keys(MODEL_PIN()) as Array<K>;
    const value = Object.values(MODEL_PIN()) as Array<T[K]>;

    key.forEach((el: K, index: number) => updateOrigin(el, value[index]));
  };

  const updateCustomFields = (value: outbound.customFields[]) => {
    state.customFields = value;
  };

  return {
    updateOrigin,
    resetOrigin,
    updateCustomFields,
  };
}

/**
 *
 * 택배다량접수 사용 되는 공통 action을 정의합니다.
 */
export function useMassParcelAction() {
  const { updateOrigin, resetOrigin, updateCustomFields } = useMassParcelUpdate();
  const { getClientInfo } = useClient();
  /**
   * 출발지 정보 로그인한 사용자 정보로 업데이트
   */
  const actionOriginUpdateUser = async () => {
    try {
      const { client, center } = await getClientInfo();
      updateOrigin('name', client['name']);
      updateOrigin('phoneNumber', {
        value: client['phoneNumber'],
        isValidate: true,
      });
      updateOrigin('dongName', center['dongName']);
      updateOrigin('jibunAddress', center['jibunAddress']);
      updateOrigin('roadAddress', center['roadAddress']);
      updateOrigin('detailAddress', center['detailAddress']);
      updateOrigin('latitude', center['latitude']);
      updateOrigin('longitude', center['longitude']);
      updateOrigin('postalCode', center['postalCode']);
    } catch {}
  };

  const resetValidateInfo = () => {
    store.dispatch('outboundParcel/resetValidateInfo');
  };

  /**
   * onUnmounted에 전체 초기화
   */
  const actionOnUnmouned = () => {
    state.isBusy = false;
    state.policy = false;
    state.list = [];
    resetOrigin();
    resetValidateInfo();
    updateCustomFields([]);
  };

  return {
    actionOriginUpdateUser,
    actionOnUnmouned,
    resetValidateInfo,
  };
}

/**
 * 택태다량접수 getters
 */
export function useMassParcelGetters() {
  /**
   * 발송리스트 중 에러가 발생한 목록이 있는지 확인합니다.
   */
  const getListHaveError = computed(() => {
    for (let i = 0; i < state.list.length; i++) {
      if (state.list[i].isError === true) return true;
    }
    return false;
  });

  /**
   * 계산 완료된 건
   */
  const getChargeCount = computed(() => {
    return state.list.reduce((stack, el, i) => {
      if (el.isError === true) return stack;
      else return (stack += 1);
    }, 0);
  });

  /**
   * 계산 완료된 총 요금
   */
  const getChargeTotal = computed(() => {
    return state.list.reduce((stack, el, i) => {
      if (!el.chargeUuid) return stack;
      else return stack + el.totalCharge;
    }, 0);
  });

  /**
   * 계산 완료된 기본 요금
   */
  const getChargeBasic = computed(() => {
    return state.list.reduce((stack, el, i) => {
      if (!el.chargeUuid) return stack;
      else return stack + (el.baseCharge - Math.floor(el.baseCharge / 11));
    }, 0);
  });

  /**
   * 계산 완료된 도선료
   */
  const getChargeFerry = computed(() => {
    return state.list.reduce((stack, el, i) => {
      if (!el.chargeUuid) return stack;
      else return stack + el.zoneCharge + el.ferryCharge;
    }, 0);
  });

  /**
   * 계산 완료된 부가세
   */
  const getChargeVat = computed(() => {
    return state.list.reduce((stack, el, i) => {
      if (!el.chargeUuid) return stack;
      else return stack + Math.floor(el.baseCharge / 11);
    }, 0);
  });
  return {
    getListHaveError,
    getChargeCount,
    getChargeTotal,
    getChargeBasic,
    getChargeFerry,
    getChargeVat,
  };
}
